"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const BodyResourceBoundIriTemplateExpansionStrategy_1 = require("./BodyResourceBoundIriTemplateExpansionStrategy");
const HydraClient_1 = require("./HydraClient");
const CompoundGraphTransformer_1 = require("./JsonLd/GraphTransformations/CompoundGraphTransformer");
const EntryPointCorrectingGraphTransformer_1 = require("./JsonLd/GraphTransformations/EntryPointCorrectingGraphTransformer");
const FlatteningGraphTransformer_1 = require("./JsonLd/GraphTransformations/FlatteningGraphTransformer");
const IndirectTypingProvider_1 = require("./JsonLd/IndirectTypingProvider");
const JsonLdHypermediaProcessor_1 = require("./JsonLd/JsonLdHypermediaProcessor");
const StaticOntologyProvider_1 = require("./JsonLd/StaticOntologyProvider");
const LinksPolicy_1 = require("./LinksPolicy");
/* tslint:disable:no-var-requires */
const hydraOntology = require("./JsonLd/hydra.json");
/**
 * Provides a factory of the {@link IHydraClient}s.
 */
class HydraClientFactory {
    constructor(hypermediaProcessorFactories, iriTemplateExpansionStrategy, linksPolicy, httpCall) {
        this.linksPolicy = LinksPolicy_1.LinksPolicy.Strict;
        this.httpCall = null;
        this.hypermediaProcessorFactories = hypermediaProcessorFactories || [];
        this.iriTemplateExpansionStrategy = iriTemplateExpansionStrategy || null;
        this.linksPolicy = linksPolicy || LinksPolicy_1.LinksPolicy.Strict;
        this.httpCall = httpCall || null;
    }
    /**
     * Starts the factory configuration.
     * @returns {HydraClientFactory}
     */
    static configure() {
        return new HydraClientFactory();
    }
    static createJsonLdHypermediaProcessor(httpCall) {
        const graphTransformer = new CompoundGraphTransformer_1.default(new FlatteningGraphTransformer_1.default(), new EntryPointCorrectingGraphTransformer_1.default());
        return new JsonLdHypermediaProcessor_1.default(new IndirectTypingProvider_1.default(new StaticOntologyProvider_1.default(hydraOntology)), httpCall, graphTransformer);
    }
    /** @inheritDoc */
    get currentHttpCall() {
        return this.httpCall;
    }
    /** @inheritDoc */
    get currentLinksPolicy() {
        return this.linksPolicy;
    }
    /** @inheritDoc */
    createProcessorToHandle(mediaType) {
        if (!!mediaType) {
            for (const hypermediaProcessor of this.resolveProcessors()) {
                for (const supportedMediaType of hypermediaProcessor.supportedMediaTypes) {
                    if (supportedMediaType === mediaType) {
                        return hypermediaProcessor;
                    }
                }
            }
        }
        throw new Error(HydraClient_1.default.noHypermediaProcessors);
    }
    /**
     * Configures a future {@link IHydraClient} with {@link JsonLdHypermediaProcessor},
     * {@link BodyResourceBoundIriTemplateExpansionStrategy} and fetch components.
     * @returns {HydraClientFactory}
     */
    withDefaults() {
        return this.with(new BodyResourceBoundIriTemplateExpansionStrategy_1.default())
            .withStrictLinks()
            .with(fetch.bind(window))
            .withJsonLd();
    }
    /**
     * Configures a factory to create a client with explicitly defined links.
     * @returns {HydraClientFactory}
     */
    withStrictLinks() {
        this.linksPolicy = LinksPolicy_1.LinksPolicy.Strict;
        return this;
    }
    /**
     * Configures a factory to create a client with links of resources from the same host and port.
     * @returns {HydraClientFactory}
     */
    withSameRootLinks() {
        this.linksPolicy = LinksPolicy_1.LinksPolicy.SameRoot;
        return this;
    }
    /**
     * Configures a factory to create a client with all resources from HTTP/HTTPS considered links.
     * @returns {HydraClientFactory}
     */
    withAllHttpLinks() {
        this.linksPolicy = LinksPolicy_1.LinksPolicy.AllHttp;
        return this;
    }
    /**
     * Configures a factory to create a client with all resources considered links.
     * @returns {HydraClientFactory}
     */
    withAllLinks() {
        this.linksPolicy = LinksPolicy_1.LinksPolicy.All;
        return this;
    }
    /**
     * Configures a factory with JSON-LD hypermedia processor.
     * @returns {HydraClientFactory}
     */
    withJsonLd() {
        this.withFactory(context => HydraClientFactory.createJsonLdHypermediaProcessor(context.currentHttpCall));
        return this;
    }
    /**
     * Adds an another {@link IHypermediaProcessor} component via it's factory method.
     * @param {HypermediaProcessorFactory} hypermediaProcessorFactory Hypermedia processor factory to be passed
     *                                                                to future {@link HydraClient} instances.
     * @returns {HydraClientFactory}
     */
    withFactory(hypermediaProcessorFactory) {
        this.hypermediaProcessorFactories.push(hypermediaProcessorFactory);
        return this;
    }
    /* tslint:disable:ban-types */
    with(component) {
        if (typeof component.createRequest === "function") {
            this.iriTemplateExpansionStrategy = component;
        }
        else if (typeof component.process === "function") {
            this.hypermediaProcessorFactories.push(() => component);
        }
        else {
            this.httpCall = component;
        }
        return this;
    }
    /**
     * Creates a new instance of the {@link IHydraClient}.
     * @returns {IHydraClient}
     */
    andCreate() {
        return new HydraClient_1.default(this.resolveProcessors(), this.iriTemplateExpansionStrategy, this.linksPolicy, this.httpCall);
    }
    resolveProcessors() {
        const result = [];
        for (const factoryMethod of this.hypermediaProcessorFactories) {
            const factory = factoryMethod;
            if (typeof factory.instance === "undefined") {
                const childContext = new HydraClientFactory(this.hypermediaProcessorFactories.filter(_ => _ !== factoryMethod), this.iriTemplateExpansionStrategy, this.linksPolicy, this.httpCall);
                Object.defineProperty(factory, "instance", { value: factoryMethod(childContext), enumerable: false });
            }
            result.push(factory.instance);
        }
        return result;
    }
}
exports.default = HydraClientFactory;
//# sourceMappingURL=HydraClientFactory.js.map